import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, RotateCcw, MessageCircle, Radar } from "lucide-react";
import { SectionLabel, MaskedLine } from "@/components/Reveal";
import { Radar4D, ScoreBars } from "@/components/Radar4D";
import { WPP_URL } from "@/links";
import { trackEvent } from "@/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { DIMENSIONS, level } from "@/diagnosticData";

const inputCls =
  "w-full border border-white/15 bg-ink/60 px-5 py-4 text-sm text-white placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-brand-orange";

const Question = ({ dimIdx, qIdx, text, value, onAnswer }) => (
  <div data-testid={`diag-question-${dimIdx}-${qIdx}`} className="border-b border-white/10 py-7 last:border-0">
    <p className="max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg">{text}</p>
    <div className="mt-4 flex gap-2">
      {[1, 2, 3, 4, 5].map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onAnswer(dimIdx, qIdx, v)}
          data-testid={`diag-option-${dimIdx}-${qIdx}-${v}`}
          className={`h-11 flex-1 border font-mono text-sm transition-all duration-300 ${
            value === v
              ? "border-brand-orange bg-brand-orange text-white"
              : "border-white/15 text-slate-400 hover:border-brand-orange/60 hover:text-white"
          }`}
        >
          {v}
        </button>
      ))}
    </div>
    <div className="mt-2 flex justify-between font-mono text-[9px] uppercase tracking-[0.18em] text-slate-600">
      <span>Discordo totalmente</span>
      <span>Concordo totalmente</span>
    </div>
  </div>
);

const Intro = ({ onStart }) => (
  <motion.div
    key="intro"
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -24 }}
    transition={{ duration: 0.6 }}
    className="mx-auto max-w-3xl text-center"
  >
    <div className="flex justify-center">
      <SectionLabel>Diagnóstico 4D</SectionLabel>
    </div>
    <h1 className="mt-8 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
      <MaskedLine delay={0.15}>Descubra o índice de</MaskedLine>
      <MaskedLine delay={0.3}>
        <span className="font-serif font-medium italic text-brand-orange">maturidade</span> da sua marca.
      </MaskedLine>
    </h1>
    <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-slate-400">
      20 afirmações sobre negócio, marca, cliente, mercado e comunicação. Em cerca de 4 minutos
      você vê o seu radar estratégico na tela, sem sair da página.
    </p>
    <button
      onClick={onStart}
      data-testid="diag-start-button"
      className="group mt-10 inline-flex items-center gap-3 bg-brand-orange px-10 py-5 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-brand-red"
    >
      Começar o diagnóstico
      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
    </button>
    <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
      Leitura preliminar — o Diagnóstico Executivo D1 aprofunda com entrevistas e análise.
    </p>
  </motion.div>
);

const LeadStep = ({ lead, setLead, onSubmit, onSkip, sending }) => (
  <motion.div
    key="lead"
    initial={{ opacity: 0, x: 40 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -40 }}
    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    className="mx-auto w-full max-w-xl"
  >
    <div className="flex justify-center">
      <span className="flex h-14 w-14 items-center justify-center border border-brand-orange/50 text-brand-orange">
        <Radar size={24} />
      </span>
    </div>
    <h2 className="mt-8 text-center font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
      Seu radar está <span className="font-serif font-medium italic text-brand-orange">pronto.</span>
    </h2>
    <p className="mx-auto mt-5 max-w-md text-center text-sm leading-relaxed text-slate-400">
      Deixe seu nome e e-mail para a Danielle saber de quem é este radar e poder devolver
      uma leitura comentada na reunião estratégica.
    </p>
    <form onSubmit={onSubmit} data-testid="diag-lead-form" className="mt-10 space-y-5">
      <label className="block">
        <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Nome</span>
        <input
          required
          value={lead.name}
          onChange={(e) => setLead({ ...lead, name: e.target.value })}
          data-testid="diag-lead-name"
          className={inputCls}
          placeholder="Seu nome"
        />
      </label>
      <label className="block">
        <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">E-mail</span>
        <input
          required
          type="email"
          value={lead.email}
          onChange={(e) => setLead({ ...lead, email: e.target.value })}
          data-testid="diag-lead-email"
          className={inputCls}
          placeholder="voce@empresa.com.br"
        />
      </label>
      <label className="block">
        <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
          WhatsApp <span className="text-slate-600">(opcional)</span>
        </span>
        <input
          value={lead.whatsapp}
          onChange={(e) => setLead({ ...lead, whatsapp: e.target.value })}
          data-testid="diag-lead-whatsapp"
          className={inputCls}
          placeholder="(47) 99999-0000"
        />
      </label>
      <button
        type="submit"
        disabled={sending}
        data-testid="diag-lead-submit"
        className="group flex w-full items-center justify-center gap-3 bg-brand-orange px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-brand-red disabled:opacity-60"
      >
        {sending ? "Gerando radar..." : "Ver meu radar estratégico"}
        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
      </button>
      <button
        type="button"
        onClick={onSkip}
        data-testid="diag-lead-skip"
        className="mx-auto block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600 transition-colors hover:text-slate-300"
      >
        Ver sem me identificar
      </button>
    </form>
  </motion.div>
);

const Result = ({ dimScores, lead, onRestart }) => {
  const overall = Math.round(dimScores.reduce((a, b) => a + b, 0) / dimScores.length);
  const radarData = DIMENSIONS.map((d, i) => ({ dim: d.short, valor: dimScores[i] }));
  const weakestIdx = dimScores.indexOf(Math.min(...dimScores));
  const weakest = DIMENSIONS[weakestIdx].title;
  const [overallLabel, overallColor] = level(overall);

  const recommendation =
    overall < 45
      ? "O ponto de partida é o D1 Diagnóstico completo. Antes de investir em comunicação, é preciso um espelho imparcial do negócio: onde a marca está de verdade e o que está travando a percepção de valor."
      : overall < 70
        ? `O ponto de partida é o D1 Diagnóstico, com atenção especial a ${weakest} — hoje a dimensão mais frágil do seu radar. É ali que a percepção de valor está vazando.`
        : "Sua base é sólida. A conversa agora é de Direção (D2): afiar o posicionamento para transformar vantagem real em demanda qualificada — e defender preço sem esforço.";

  const firstName = lead.name ? lead.name.trim().split(" ")[0] : "";
  const wppText = encodeURIComponent(
    `Olá, Danielle! ${firstName ? `Aqui é ${firstName}. ` : ""}Fiz o Diagnóstico 4D no site da FUCHS.\nÍndice geral: ${overall}/100\n${radarData.map((d) => `${d.dim} ${d.valor}`).join(" · ")}\nQuero conversar sobre o resultado.`
  );

  return (
    <motion.div
      key="result"
      data-testid="diag-result"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
      className="mx-auto grid w-full max-w-6xl gap-14 lg:grid-cols-2"
    >
      <div>
        <SectionLabel>{firstName ? `Seu resultado, ${firstName}` : "Seu resultado"}</SectionLabel>
        <div className="mt-8 flex items-end gap-5">
          <motion.p
            data-testid="diag-overall-score"
            className="font-display text-8xl font-extrabold leading-none text-white lg:text-9xl"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            {overall}
          </motion.p>
          <div className="pb-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">/ 100</p>
            <p className={`mt-1 font-mono text-xs uppercase tracking-[0.25em] ${overallColor}`}>{overallLabel}</p>
          </div>
        </div>
        <p className="mt-8 max-w-lg font-serif text-lg italic leading-relaxed text-slate-300">{recommendation}</p>
        <div className="mt-8 space-y-3">
          {radarData.map((d) => {
            const [l, c] = level(d.valor);
            return (
              <div key={d.dim} className="flex items-center justify-between border-b border-white/10 pb-3">
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">{d.dim}</span>
                <span className="flex items-baseline gap-3">
                  <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${c}`}>{l}</span>
                  <span className="font-display text-xl font-bold text-white">{d.valor}</span>
                </span>
              </div>
            );
          })}
        </div>
        <div className="mt-10 flex flex-col gap-4 sm:flex-row">
          <a
            href={`${WPP_URL}?text=${wppText}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="diag-whatsapp-cta"
            className="group inline-flex items-center justify-center gap-3 bg-brand-orange px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-brand-red"
          >
            <MessageCircle size={16} />
            Conversar sobre o resultado
          </a>
          <button
            onClick={onRestart}
            data-testid="diag-restart-button"
            className="inline-flex items-center justify-center gap-3 border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:border-brand-orange hover:text-brand-orange"
          >
            <RotateCcw size={15} />
            Refazer
          </button>
        </div>
      </div>
      <div className="border border-white/10 bg-navy/40 p-6 backdrop-blur-sm lg:p-10">
        <Radar4D data={radarData} caption="Seu radar estratégico" />
        <div className="mt-8 border-t border-white/10 pt-8">
          <ScoreBars data={radarData} caption="Índice de maturidade 0–100 · Leitura preliminar" />
        </div>
      </div>
    </motion.div>
  );
};

export default function Diagnostico() {
  const [step, setStep] = useState(0); // 0 intro, 1..5 dims, 6 lead, 7 result
  const [answers, setAnswers] = useState(DIMENSIONS.map((d) => d.questions.map(() => null)));
  const [lead, setLead] = useState({ name: "", email: "", whatsapp: "" });
  const [sending, setSending] = useState(false);
  const saved = useRef(false);
  const [params] = useSearchParams();
  const source = params.get("fonte") || "";

  const answer = (di, qi, v) =>
    setAnswers((prev) => prev.map((arr, i) => (i === di ? arr.map((x, j) => (j === qi ? v : x)) : arr)));

  const dimIdx = step - 1;
  const currentDone = step >= 1 && step <= 5 && answers[dimIdx].every((v) => v !== null);
  const answeredTotal = answers.flat().filter((v) => v !== null).length;

  const computeScores = () =>
    answers.map((arr) => Math.round(((arr.reduce((a, b) => a + b, 0) / arr.length - 1) / 4) * 100));

  const saveAndShow = async (withLead) => {
    if (!saved.current) {
      saved.current = true;
      setSending(true);
      const dimScores = computeScores();
      const overall = Math.round(dimScores.reduce((a, b) => a + b, 0) / dimScores.length);
      try {
        await axios.post(`${API}/diagnostics`, {
          scores: Object.fromEntries(DIMENSIONS.map((d, i) => [d.short, dimScores[i]])),
          overall,
          source,
          ...(withLead ? lead : {}),
        });
        trackEvent("diagnostic_completed", { source: source || "direto" });
      } catch {
        // resultado continua visível mesmo se o registro falhar
      } finally {
        setSending(false);
      }
    }
    setStep(7);
  };

  const restart = () => {
    setAnswers(DIMENSIONS.map((d) => d.questions.map(() => null)));
    setLead({ name: "", email: "", whatsapp: "" });
    saved.current = false;
    setStep(0);
  };

  const dimScores = answers.map((arr) =>
    arr.every((v) => v !== null)
      ? Math.round(((arr.reduce((a, b) => a + b, 0) / arr.length - 1) / 4) * 100)
      : 0
  );

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="hero-glow noise relative min-h-screen bg-ink px-6 pb-24 pt-32 lg:px-10 lg:pt-40"
    >
      {step >= 1 && step <= 5 && (
        <div data-testid="diag-progress" className="mx-auto mb-10 max-w-3xl">
          <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            <span>
              Etapa {step} de 5 — {DIMENSIONS[dimIdx].title}
            </span>
            <span>{answeredTotal}/20</span>
          </div>
          <div className="mt-3 h-[3px] w-full bg-white/10">
            <motion.div
              className="h-full bg-gradient-to-r from-brand-orange to-brand-magenta"
              animate={{ width: `${(answeredTotal / 20) * 100}%` }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            />
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        {step === 0 && <Intro key="intro" onStart={() => setStep(1)} />}

        {step >= 1 && step <= 5 && (
          <motion.div
            key={`dim-${step}`}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto w-full max-w-3xl"
          >
            <div className="flex items-baseline gap-5">
              <span className="font-display text-5xl font-extrabold text-outline-orange lg:text-6xl">
                0{step}
              </span>
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                {DIMENSIONS[dimIdx].title}
              </h2>
            </div>
            <p className="mt-3 text-sm text-slate-500">
              Avalie cada afirmação de 1 a 5, pensando na realidade de hoje — não na intenção.
            </p>
            <div className="mt-8">
              {DIMENSIONS[dimIdx].questions.map((q, qi) => (
                <Question
                  key={qi}
                  dimIdx={dimIdx}
                  qIdx={qi}
                  text={q}
                  value={answers[dimIdx][qi]}
                  onAnswer={answer}
                />
              ))}
            </div>
            <div className="mt-10 flex items-center justify-between">
              <button
                onClick={() => setStep(step - 1)}
                data-testid="diag-back-button"
                className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400 transition-colors hover:text-white"
              >
                <ArrowLeft size={14} /> Voltar
              </button>
              <button
                onClick={() => (step < 5 ? setStep(step + 1) : setStep(6))}
                disabled={!currentDone}
                data-testid="diag-next-button"
                className="group flex items-center gap-3 bg-brand-orange px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-brand-red disabled:cursor-not-allowed disabled:opacity-40"
              >
                {step < 5 ? "Avançar" : "Ver meu resultado"}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </motion.div>
        )}

        {step === 6 && (
          <LeadStep
            key="lead"
            lead={lead}
            setLead={setLead}
            sending={sending}
            onSubmit={(e) => {
              e.preventDefault();
              saveAndShow(true);
            }}
            onSkip={() => saveAndShow(false)}
          />
        )}

        {step === 7 && <Result key="result" dimScores={dimScores} lead={lead} onRestart={restart} />}
      </AnimatePresence>

      {step !== 7 && (
        <p className="mx-auto mt-16 max-w-3xl text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
          Prefere conversar antes?{" "}
          <Link to="/contato" data-testid="diag-contact-link" className="text-brand-orange hover:underline">
            Fale com a Fuchs
          </Link>
        </p>
      )}
    </motion.main>
  );
}
